import { type CountryCode, type ProjectCode } from '@constants'
import { useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { useEffect, useState } from 'react'
import DeletableChip from '../DeletableChip/DeletableChip'
import TitleBar from '../TitleBar/TitleBar'
import style from './Searcher.module.scss'
import SearcherRecentKeywordDisplayToggleButton from './SearcherRecentKeywordDisplayToggleButton'

const DISPLAY_CHIP_PER_ROW_HEIGHT = 2.4
const MAX_DISPLAY_ROW_COUNT = 3

type FoldMode = 'FOLD_MODE_FOLDED' | 'FOLD_MODE_UNFOLDED'

interface SearcherRecentKeywordDisplayProps {
  setUserInputFieldTextAndSearch: (keyword: string) => void
  searchKeywords: string[]
  clearSearchHistory: () => void
  removeSearchKey: (keyword: string) => void
  onPastResultClick?: (past_keyword: string) => void
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignId: number
  environment?: 'campaign' | 'merchant'
}

const getChipAlignmentRowCount = (keywords: string[]): number => {
  const rects: DOMRect[] = []
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < keywords.length; i++) {
    // @ts-expect-error - TODO: fix this
    const returnedVal = document.getElementById(keywords[i])?.getBoundingClientRect()
    if (returnedVal !== undefined) rects.push(returnedVal)
  }

  let rowCount = 1
  rects.sort((a, b) => (a.top < b.top ? -1 : 1))
  for (let i = 1; i < rects.length; i++) {
    // @ts-expect-error - TODO: fix this
    if (rects[i].top !== rects[i - 1].top) {
      rowCount++
      if (rowCount === MAX_DISPLAY_ROW_COUNT) {
        break
      }
    }
  }
  return rowCount
}

const SearcherRecentKeywordDisplay = ({
  removeSearchKey,
  searchKeywords,
  clearSearchHistory,
  setUserInputFieldTextAndSearch,
  onPastResultClick,
  projectCode,
  countryCode,
  campaignId,
  environment,
}: SearcherRecentKeywordDisplayProps) => {
  const [keywords, setKeywords] = useState<string[]>(searchKeywords)
  const [curFoldMode, setFoldMode] = useState<string>('FOLD_MODE_FOLDED')
  const [height, setHeight] = useState<number>(DISPLAY_CHIP_PER_ROW_HEIGHT)
  const [chipRowCount, setChipRowCount] = useState<number>(1)
  const { sendClickPastResultEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const setChipPanelHeight = (curFoldMode: string) => {
    const rowCount = getChipAlignmentRowCount(keywords)
    if (curFoldMode === 'FOLD_MODE_FOLDED') {
      setHeight(DISPLAY_CHIP_PER_ROW_HEIGHT)
    }
    else if (curFoldMode === 'FOLD_MODE_UNFOLDED') {
      setHeight(DISPLAY_CHIP_PER_ROW_HEIGHT * rowCount)
    }
  }

  useEffect(() => {
    setKeywords(searchKeywords)
    // setTimeout must be used here to wait for deletableChips to finish rendering.
    // if setTimeout is not used here, when randomly deleting chip in between, the unable-update-height behavior of this ui is observed.
    setTimeout(() => {
      const count = getChipAlignmentRowCount(keywords)
      setChipRowCount(count)
      setChipPanelHeight(count === 1 ? 'FOLD_MODE_FOLDED' : curFoldMode)
    }, 400)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeywords])
  const titleBarInfo = <div className={style.title}>最近搜尋</div>
  const titleBarButton = (
    <Button
      variant="text"
      color="grey"
      onClick={() => {
        clearSearchHistory()
        setKeywords([])
        setUserInputFieldTextAndSearch('')
      }}
    >
      清除搜尋記錄
    </Button>
  )

  const handleKeywordClick = useCallbackEvent((keyword: string) => {
    if (environment === 'campaign') {
      sendClickPastResultEvent({ pastKeyword: keyword })
    }
    setUserInputFieldTextAndSearch(keyword)
    onPastResultClick?.(keyword)
  })

  return (
    <div>
      {keywords.length > 0 && (
        <section>
          <TitleBar infoComponent={titleBarInfo} buttonComponent={titleBarButton} />
          <div className={style.keywords} style={{ height: height + 'rem' }}>
            {keywords?.map((keyword) => {
              return (
                <DeletableChip
                  key={keyword}
                  chipKey={keyword}
                  chipId={keyword}
                  onDelete={() => {
                    removeSearchKey(keyword)
                  }}
                  onClick={() => handleKeywordClick(keyword)}
                  label={keyword.length > 10 ? keyword.substring(0, 10) + '...' : keyword}
                />
              )
            })}
          </div>
          {chipRowCount > 1 && (
            <SearcherRecentKeywordDisplayToggleButton
              onToggle={() => {
                const foldMode
                  = curFoldMode === 'FOLD_MODE_UNFOLDED'
                    ? 'FOLD_MODE_FOLDED'
                    : 'FOLD_MODE_UNFOLDED'
                setFoldMode(foldMode)
                setChipPanelHeight(foldMode)
              }}
              currentFoldMode={curFoldMode}
            />
          )}
        </section>
      )}
    </div>
  )
}

export { SearcherRecentKeywordDisplay, type FoldMode }
